import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Button } from "./ui/button";
import { toast } from "sonner";
import { Bug, Lightbulb, MapPin, MessageCircle, Mail, Trash2, CheckCircle2, Eye, RefreshCw } from "lucide-react";

const CATEGORY_META = {
  bug:        { label: "Bug",        Icon: Bug,           tint: "bg-red-100 text-red-700" },
  feature:    { label: "Feature",    Icon: Lightbulb,     tint: "bg-amber-100 text-amber-700" },
  venue_info: { label: "Venue info", Icon: MapPin,        tint: "bg-blue-100 text-blue-700" },
  other:      { label: "Other",      Icon: MessageCircle, tint: "bg-neutral-100 text-neutral-700" },
};

const STATUS_TABS = [
  { id: "new", label: "New" },
  { id: "read", label: "Read" },
  { id: "resolved", label: "Resolved" },
];

export default function AdminFeedback() {
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({ new: 0, read: 0, resolved: 0 });
  const [statusFilter, setStatusFilter] = useState("new");
  const [loading, setLoading] = useState(false);

  const load = async (status = statusFilter) => {
    setLoading(true);
    try {
      const r = await api.get(`/admin/feedback?status=${status}`);
      setItems(r.data.items || []);
      setCounts(r.data.counts || { new: 0, read: 0, resolved: 0 });
    } catch {
      toast.error("Failed to load feedback");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(statusFilter); }, [statusFilter]);

  const setStatus = async (id, next) => {
    try {
      await api.post(`/admin/feedback/${id}/status`, { status: next });
      toast.success(next === "resolved" ? "Marked resolved" : next === "read" ? "Marked read" : "Reopened");
      load(statusFilter);
    } catch {
      toast.error("Failed to update");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this feedback? This cannot be undone.")) return;
    try {
      await api.delete(`/admin/feedback/${id}`);
      toast.success("Deleted");
      load(statusFilter);
    } catch {
      toast.error("Failed to delete");
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-feedback">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex gap-2">
          {STATUS_TABS.map((t) => {
            const active = statusFilter === t.id;
            const count = counts[t.id] || 0;
            return (
              <button
                key={t.id}
                onClick={() => setStatusFilter(t.id)}
                data-testid={`fb-filter-${t.id}`}
                className={`px-4 py-1.5 rounded-full text-sm border transition-colors ${
                  active
                    ? "bg-navy text-white border-navy"
                    : "bg-white text-neutral-700 border-black/10 hover:border-navy/40"
                }`}
              >
                {t.label}
                <span className={`ml-2 text-xs ${active ? "text-white/70" : "text-neutral-500"}`}>{count}</span>
              </button>
            );
          })}
        </div>
        <Button size="sm" variant="outline" onClick={() => load(statusFilter)} className="rounded-full" data-testid="fb-refresh-btn">
          <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Refresh
        </Button>
      </div>

      {loading ? (
        <p className="text-neutral-500">Loading...</p>
      ) : items.length === 0 ? (
        <p className="text-neutral-500 py-8 text-center bg-white border border-dashed border-black/10 rounded-2xl">
          No {statusFilter} feedback right now.
        </p>
      ) : (
        <div className="space-y-3">
          {items.map((f) => {
            const meta = CATEGORY_META[f.category] || CATEGORY_META.other;
            const when = new Date(f.created_at).toLocaleString(undefined, {
              month: "short", day: "numeric", hour: "2-digit", minute: "2-digit",
            });
            return (
              <div
                key={f.id}
                className="bg-white border border-black/5 rounded-2xl p-5"
                data-testid={`fb-item-${f.id}`}
              >
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div className="flex items-center gap-3">
                    <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${meta.tint}`}>
                      <meta.Icon className="w-3.5 h-3.5" /> {meta.label}
                    </div>
                    <span className="text-xs text-neutral-500">{when}</span>
                    {f.status === "resolved" && (
                      <span className="inline-flex items-center gap-1 text-xs text-green-700">
                        <CheckCircle2 className="w-3.5 h-3.5" /> Resolved
                      </span>
                    )}
                  </div>
                  <div className="flex gap-2">
                    {f.status !== "read" && f.status !== "resolved" && (
                      <Button size="sm" variant="outline" onClick={() => setStatus(f.id, "read")} className="rounded-full" data-testid={`fb-mark-read-${f.id}`}>
                        <Eye className="w-3.5 h-3.5 mr-1" /> Mark read
                      </Button>
                    )}
                    {f.status !== "resolved" && (
                      <Button size="sm" onClick={() => setStatus(f.id, "resolved")} className="rounded-full bg-navy hover:bg-navy/90" data-testid={`fb-resolve-${f.id}`}>
                        <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Resolve
                      </Button>
                    )}
                    {f.status === "resolved" && (
                      <Button size="sm" variant="outline" onClick={() => setStatus(f.id, "new")} className="rounded-full" data-testid={`fb-reopen-${f.id}`}>
                        Reopen
                      </Button>
                    )}
                    <Button size="sm" variant="ghost" onClick={() => remove(f.id)} className="rounded-full text-red-600 hover:bg-red-50 hover:text-red-700" data-testid={`fb-delete-${f.id}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>

                <p className="mt-4 text-neutral-800 leading-relaxed whitespace-pre-line">{f.message}</p>

                <div className="mt-4 pt-3 border-t border-black/5 flex items-center gap-4 text-xs text-neutral-500 flex-wrap">
                  {f.name && <span>From <span className="text-neutral-700 font-medium">{f.name}</span></span>}
                  {f.email && (
                    <a href={`mailto:${f.email}?subject=Re: your inAirlie feedback`} className="inline-flex items-center gap-1 text-navy hover:underline" data-testid={`fb-reply-${f.id}`}>
                      <Mail className="w-3.5 h-3.5" /> {f.email}
                    </a>
                  )}
                  {f.page_url && <span className="truncate max-w-xs" title={f.page_url}>via {f.page_url}</span>}
                  {!f.email && !f.name && <span className="italic text-neutral-400">Anonymous</span>}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
